import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import get from 'lodash/get'
import * as Sentry from '@sentry/react'

import { getPermission, PERMISSION_STATES, PERMISSIONS } from '../../utils/permissions-service'
import { getGeolocation } from '../../utils/geolocation'
import { onClearGeolocation, selectGeolocation } from '../../root/reducers/shipments/geolocation'

const isValidLatLng = (latLng) => {
  if (!latLng) return false
  const parts = latLng.split(',')
  if (parts.length !== 2) return false
  const lat = parseFloat(parts[0])
  const lng = parseFloat(parts[1])
  return !isNaN(lat) && !isNaN(lng)
}

const onGetGeolocation = async ({ permission, dispatch, watchId, callback = null, maximumAge = 5000 }) => {
  const configGeolocationOptions = { maximumAge } // default to 5 seconds (ms)

  const isPromptFlow = permission === PERMISSION_STATES.PROMPT && typeof callback === 'function' && !watchId

  // Fire the UI callback as soon as the user responds to the browser prompt,
  // rather than waiting for a position fix. getCurrentPosition can take a
  // long time (or time out) even after permission is granted, which used to
  // leave the permission modal stuck on its loading state.
  let callbackFired = false
  const fireCallback = () => {
    if (callbackFired) return
    callbackFired = true
    callback()
  }

  let permissionStatus
  const onPermissionChange = () => {
    if (permissionStatus.state !== PERMISSION_STATES.PROMPT) {
      permissionStatus.removeEventListener('change', onPermissionChange)
      fireCallback()
    }
  }

  if (isPromptFlow && 'permissions' in navigator) {
    try {
      permissionStatus = await navigator.permissions.query({ name: PERMISSIONS.GEOLOCATION })
      if (permissionStatus.state !== PERMISSION_STATES.PROMPT) {
        fireCallback()
      } else {
        permissionStatus.addEventListener('change', onPermissionChange)
      }
    } catch (error) {
      console.warn(error)
    }
  }

  // We need to pass dispatch in order to update the redux store
  try {
    const result = await getGeolocation(permission, dispatch, configGeolocationOptions)

    // Fallback for environments without the Permissions API: close the UI
    // once we get the first position back from getCurrentPosition.
    if (isPromptFlow) {
      fireCallback()
    }

    // Start watching position if permission is granted after initial prompt
    // Don't await - watchPosition never resolves as it's a continuous watcher
    if (result.newPermission === PERMISSION_STATES.GRANTED && result.newPermission !== permission) {
      getGeolocation(result.newPermission, dispatch, configGeolocationOptions).catch(err => {
        console.warn('[GPS] watchPosition error:', err)
      })
    }
  } catch (error) {
    console.warn(error)
    // Still call callback on error so the UI doesn't hang
    if (isPromptFlow) {
      fireCallback()
    }
  }
}

const useGeolocation = (api, shipment) => {
  const dispatch = useDispatch()
  const geolocationData = useSelector(selectGeolocation)

  const [locationObject, setLocationObject] = useState()
  const [distance, setDistance] = useState()
  const [loading, setLoading] = useState(true)
  const [hasValidFacilityCoordinates, setHasValidFacilityCoordinates] = useState(true)

  const { position, watchId } = geolocationData

  const onGetCurrentDistance = async () => {
    const latLng = get(locationObject, 'additionalData.latLng')

    if (!isValidLatLng(latLng)) {
      setLoading(false)
      return
    }

    const [latStr, lngStr] = latLng.split(',')

    try {
      const distance = await api.shipment.calculateDistance(
        { lat: position.lat, long: position.lng }, // from point A coordinates
        { lat: Number(latStr), long: Number(lngStr) } // to point B coordinates
      )
      const accuracy = get(geolocationData, 'geoPosition.coords.accuracy')
      const breadcrumbData = {
        distanceKm: distance,
        driverLat: position.lat,
        driverLng: position.lng,
        facilityLat: Number(latStr),
        facilityLng: Number(lngStr),
        accuracy: accuracy != null ? `${Math.round(accuracy)}m` : 'unknown'
      }
      console.debug('[GPS] Distance calculated', breadcrumbData)
      Sentry.addBreadcrumb({
        category: 'gps',
        message: 'Distance calculated',
        data: breadcrumbData,
        level: 'info'
      })
      setDistance(distance)
    } catch (error) {
      console.warn(error)
    } finally {
      setLoading(false)
    }
  }

  const onHandleGetCurrentDistance = async () => {
    if (position.lat && position.lng && locationObject) {
      await onGetCurrentDistance()
    } else {
      setLoading(false)
    }
  }

  const onHandleGetGeolocation = async () => {
    const locationId = get(shipment, 'destination.id', '')
    let permission

    try {
      let locationObject = await api.location.get(locationId)
      if (!locationObject) {
        // fetch from agave
        const locations = await api.location.getLocationsViaIds([locationId])
        locationObject = locations[0]
      }
      permission = await getPermission(PERMISSIONS.GEOLOCATION)
      console.debug('[GPS] Permission state on load:', permission)
      Sentry.addBreadcrumb({
        category: 'gps',
        message: 'Permission state on load',
        data: { permission },
        level: 'info'
      })
      setLocationObject(locationObject)
      setHasValidFacilityCoordinates(isValidLatLng(get(locationObject, 'additionalData.latLng')))

      if (permission && permission !== PERMISSION_STATES.PROMPT) {
        await onGetGeolocation({ permission, dispatch, watchId })
      }
    } catch (error) {
      console.warn(error)
    } finally {
      if (permission === PERMISSION_STATES.PROMPT) {
        setLoading(false)
      }
    }
  }

  const onClearGeolocationWatch = () => {
    if (watchId) {
      dispatch(onClearGeolocation(watchId))
    }
  }

  useEffect(() => {
    onHandleGetGeolocation()
  }, [])

  useEffect(() => {
    onHandleGetCurrentDistance()
    return () => onClearGeolocationWatch()
  }, [position.lat, position.lng, locationObject, watchId])

  return {
    locationObject,
    geolocationData,
    distance,
    loading,
    hasValidFacilityCoordinates
  }
}

export {
  useGeolocation,
  onGetGeolocation
}
